import { partition } from 'lodash';
import { DocType, PilotDoc, CoaDocument, DocsResponse, DocumentStatus, CoaProjectRaw, CoiDuration } from '../../types';
import {
  SortResult,
  SortedCoaDocs,
  filterAndSortCOI,
  filterAndSortP107,
  filterAndSortW9,
  getTheLatestDocAsGood,
  parseDocumentDates,
} from './filter_and_sort_utils';
import { filterAndSortCOA } from './filter_and_sort_utils/filterAndSortCOA';
import { IGNORE_FIX_REQUESTED_FOR_DOC_TYPES } from './config';

export function mapRawDocsToDocsState(docsResponse: DocsResponse) {
  // collections of all documents sorted by type
  // initial sorting for further filtering in each type
  const p107: PilotDoc[] = [];
  const coi: PilotDoc[] = [];
  const ela: PilotDoc[] = [];
  const sla: PilotDoc[] = [];
  const w9: PilotDoc[] = [];
  const tax1099: PilotDoc[] = [];

  // mixed collections of documents filtered and sorted by importance
  let goodDocs: PilotDoc[] = [];
  const needAttentionDocs: PilotDoc[] = [];
  const missedDocTypes: DocType[] = [];

  let goodCoaDocs: CoaDocument[] = [];
  const needAttentionCoaDocs: CoaDocument[] = [];

  // First step. Sort non COA documents by type without filtering
  docsResponse.nonCOADocuments.forEach((doc) => {
    switch (doc.type) {
      case DocType.REMOTE_PILOT_CERTIFICATE:
        p107.push(parseDocumentDates(doc));

        return;
      case DocType.CERTIFICATE_OF_INSURANCE:
        coi.push(parseDocumentDates(doc));

        return;
      case DocType.EQUIPMENT_LOAN_AGREEMENT:
        ela.push(parseDocumentDates(doc));

        return;
      case DocType.SERVICE_LEVEL_AGREEMENT:
        sla.push(parseDocumentDates(doc));

        return;
      case DocType.TAX_FORM_W9:
        w9.push(parseDocumentDates(doc));

        return;
      case DocType.TAX_FORM_1099:
        tax1099.push(parseDocumentDates(doc));

        return;

      default:
        return;
    }
  });

  // Second step. Filter non COA documents of each type and push them to the proper importance category
  addSortedDocs(p107, filterAndSortP107(p107));
  addSortedDocs(coi, filterAndSortCOI(coi, docsResponse.coiDuration));
  addSortedDocs(ela, getTheLatestDocAsGood(ela));
  addSortedDocs(sla, getTheLatestDocAsGood(sla));
  addSortedDocs(w9, filterAndSortW9(w9));
  // all 1099 tax forms should be shown as good
  goodDocs.push(...tax1099);

  // Third step. Parse COA projects, create a separate COA document and push them to the proper importance category
  addSortedCoaDocs(docsResponse.coaProjects);

  // Forth step. Post filtering. Move all documents marked as 'Fix requested' from Good to Need attention section.
  // For example good not expiring current or future document which was placed to the Good section
  // It was not done in sorting functions so as not spoil sorting results
  moveFixRequestedFromGoodToNeedAttention();

  return {
    goodDocs,
    needAttentionDocs,
    missedDocTypes,
    goodCoaDocs,
    needAttentionCoaDocs,
    coaProjects: docsResponse.coaProjects.map(({ coaDocuments, ...rest }) => rest),
  };

  // Functions with side effects
  function addSortedDocs(docs: PilotDoc[], sortResult: SortResult) {
    // sorter cb can skip some Fix Needed documents, so we add them after
    const sortedDocs = addSkippedFixRequested(sortResult, docs);

    goodDocs.push(...sortedDocs.good);
    needAttentionDocs.push(...sortedDocs.needAttention);

    if (sortedDocs.missedDocTypes) {
      missedDocTypes.push(sortedDocs.missedDocTypes);
    }
  }

  function addSortedCoaDocs(coaProjectsRaw: CoaProjectRaw[]) {
    const sortedCoaDocs = addSkippedCoaFixRequested(filterAndSortCOA(coaProjectsRaw), coaProjectsRaw);

    goodCoaDocs.push(...sortedCoaDocs.good);
    needAttentionCoaDocs.push(...sortedCoaDocs.needAttention);
  }

  function moveFixRequestedFromGoodToNeedAttention() {
    const [fixRequestedDocs, restGoodDocs] = partition(
      goodDocs,
      (d) => d.status === DocumentStatus.FIX_REQUESTED && !IGNORE_FIX_REQUESTED_FOR_DOC_TYPES.includes(d.type),
    );

    goodDocs = restGoodDocs;
    needAttentionDocs.push(...fixRequestedDocs);

    const [fixRequestedCoaDocs, restGoodCoaDocs] = partition(
      goodCoaDocs,
      (d) => d.document?.status === DocumentStatus.FIX_REQUESTED,
    );

    goodCoaDocs = restGoodCoaDocs;
    needAttentionCoaDocs.push(...fixRequestedCoaDocs);
  }
}

function addSkippedFixRequested(sortResult: SortResult, docs: PilotDoc[]) {
  const updatedSortResult: SortResult = { ...sortResult };

  docs.forEach((doc) => {
    if (
      doc.status === DocumentStatus.FIX_REQUESTED &&
      !(
        updatedSortResult.good.some((d) => d.documentId === doc.documentId) ||
        updatedSortResult.needAttention.some((d) => d.documentId === doc.documentId)
      )
    ) {
      updatedSortResult.needAttention.push(doc);
    }
  });

  return updatedSortResult;
}

function addSkippedCoaFixRequested(sortedCoaDocs: SortedCoaDocs, coaProjectsRaw: CoaProjectRaw[]) {
  const updatedSortCoaDocs: SortedCoaDocs = { ...sortedCoaDocs };

  coaProjectsRaw.forEach(({ coaDocuments, ...coaProjectProperties }) => {
    coaDocuments.forEach((coaDocumentRaw) => {
      if (
        coaDocumentRaw.status === DocumentStatus.FIX_REQUESTED &&
        !(
          updatedSortCoaDocs.good.some((d) => d.document?.documentId === coaDocumentRaw.documentId) ||
          updatedSortCoaDocs.needAttention.some((d) => d.document?.documentId === coaDocumentRaw.documentId)
        )
      ) {
        const document = parseDocumentDates(coaDocumentRaw);
        const needAttentionCoaDoc: CoaDocument = { ...coaProjectProperties, document };

        updatedSortCoaDocs.needAttention.push(needAttentionCoaDoc);
      }
    });
  });

  return updatedSortCoaDocs;
}
